import {Action, Reducer} from "redux";
import { all, call, fork, put, takeEvery } from 'redux-saga/effects'
import {IReportData, emptyReportData} from "../../../entities/reports/IReportData";
import {FetchState} from "../../../entities/FetchState";
import {
    callApiWithResult,
    downloadBlob,
    ICallBlobResult,
    ICallResult,
    IError
} from "../../../utils/callApi";
import Either from "../../../utils/Either";
import {emptyReportDownloadData, IReportDownloadData} from "../../../entities/reports/IReportDownloadData";

/******************************************************************
 * REPORT SPECIFIC STUFF
 *****************************************************************/
// The data to be expected
export interface IReport {
    medewerker: string;
    dossierNummer: string;
    naamClient: string;
    aanmeldingsDatum: Date;
    aanmeldingsGesprekCasDatum: Date;
    heeftContacten: boolean;
}

// API Endpoints
const reportEndpoint = "/VaphReports/GetAanmeldingen";
const reportEndpointXlsx = "/VaphReports/GetAanmeldingenXlsx";

// The action types prefix is very important to be unique to this file!
enum ActionTypes {
    FetchReport = "rapporten.vaph.FetchAanmeldingenPerPeriode Fetch",
    FetchReportXlsx =  "rapporten.vaph.FetchAanmeldingenPerPeriode FetchXlsx",
    FetchReportSuccess = "rapporten.vaph.FetchAanmeldingenPerPeriode FetchReportSuccess",
    FetchReportFailed = "rapporten.vaph.FetchAanmeldingenPerPeriode FetchReportFailed",
    FetchReportXlsxSuccess = "rapporten.vaph.FetchAanmeldingenPerPeriode FetchReportXlsxSuccess",
    FetchReportXlsxFailed = "rapporten.vaph.FetchAanmeldingenPerPeriode FetchReportXlsxFailed",
}

/**********************************************************
 * STATE
 **********************************************************/
// Define report, state and initialize state for this report
export interface IState {
    Report: IReportData<IReport>
    ReportXlsx: IReportDownloadData
}
export const InitialState: IState = {
    Report: emptyReportData(),
    ReportXlsx: emptyReportDownloadData()
}



/**********************************************************
 * ACTIONS & CREATORS
 **********************************************************/
export interface IFetchReport {
    type: ActionTypes.FetchReport,
    payload: { gebruikersId: string, startDate: Date, endDate: Date }
}
export interface IFetchReportSuccess extends Action<ActionTypes> {
    type: ActionTypes.FetchReportSuccess,
    payload: { data: IReport[] }
}
export interface IFetchReportFailed extends Action<ActionTypes> {
    type: ActionTypes.FetchReportFailed,
    payload: { errorMessage: string }
}
export interface IFetchReportXlsx {
    type: ActionTypes.FetchReportXlsx,
    payload: { gebruikersId: string, startDate: Date, endDate: Date }
}
export interface IFetchReportXlsxSuccess extends Action<ActionTypes> {
    type: ActionTypes.FetchReportXlsxSuccess,
    payload: { data: Blob }
}
export interface IFetchReportXlsxFailed extends Action<ActionTypes> {
    type: ActionTypes.FetchReportXlsxFailed,
    payload: { errorMessage: string }
}
type Actions = IFetchReport | IFetchReportSuccess | IFetchReportFailed | IFetchReportXlsx | IFetchReportXlsxSuccess | IFetchReportXlsxFailed ;
export class ActionCreators {
    public static FetchReport(userId: string, startDateIn: Date, endDateIn: Date): IFetchReport {
        return {
            type: ActionTypes.FetchReport,
            payload: { gebruikersId: userId, startDate: startDateIn, endDate: endDateIn }
        };
    }
    public static FetchReportSuccess(newData: IReport[]): IFetchReportSuccess {
        return {
            type: ActionTypes.FetchReportSuccess,
            payload: { data: newData }
        };
    }
    public static FetchReportFailed(err: string): IFetchReportFailed {
        return {
            type: ActionTypes.FetchReportFailed,
            payload: { errorMessage: err }
        };
    }
    public static FetchReportXlsx(userId: string, startDateIn: Date, endDateIn: Date): IFetchReportXlsx {
        return {
            type: ActionTypes.FetchReportXlsx,
            payload: { gebruikersId: userId, startDate: startDateIn, endDate: endDateIn }
        };
    }
    public static FetchReportXlsxSuccess(newData: Blob): IFetchReportXlsxSuccess {
        return {
            type: ActionTypes.FetchReportXlsxSuccess,
            payload: { data: newData }
        };
    }
    public static FetchReportXlsxFailed(err: string): IFetchReportXlsxFailed {
        return {
            type: ActionTypes.FetchReportXlsxFailed,
            payload: { errorMessage: err }
        };
    }
}

/******************************************************************
 * REDUCER
 *****************************************************************/
// Reducers are changing the state
export const Reducers: Reducer<IState, Actions> = (state = InitialState, action) => {
    switch (action.type) {
        case ActionTypes.FetchReport:
            return {
                ...state,
                Report: {
                    ...state.Report,
                    state: FetchState.Busy }
            };
        case ActionTypes.FetchReportSuccess:
            return {
                ...state,
                Report: {
                    ...state.Report,
                    fetchDate: new Date(),
                    state: FetchState.Success,
                    data: action.payload.data
                }
            };
        case ActionTypes.FetchReportFailed:
            return {
                ...state,
                Report: {
                    ...state.Report,
                    fetchDate: new Date(),
                    state: FetchState.Error,
                    data: [],
                    errorMessage: action.payload.errorMessage
                }
            };
        case ActionTypes.FetchReportXlsx:
            return {
                ...state,
                ReportXlsx: {
                    ...state.ReportXlsx,
                    state: FetchState.Busy }
            };
        case ActionTypes.FetchReportXlsxSuccess:
            return {
                ...state,
                ReportXlsx: {
                    ...state.ReportXlsx,
                    fetchDate: new Date(),
                    state: FetchState.Success,
                    data: action.payload.data
                }
            };
        case ActionTypes.FetchReportXlsxFailed:
            return {
                ...state,
                ReportXlsx: {
                    ...state.ReportXlsx,
                    fetchDate: new Date(),
                    state: FetchState.Error,
                    data: new Blob(),
                    errorMessage: action.payload.errorMessage
                }
            };
        default:
            return state;
    }
}


/******************************************************************
 * SAGAS
 *****************************************************************/
function* handleIFetchReport(action: IFetchReport) {
    const response: Either<IError, ICallResult<IReport[]>> = yield call(callApiWithResult, 'get', `${reportEndpoint}?gebruikerId=${action.payload.gebruikersId}&startTime=${encodeURI(action.payload.startDate.toISOString())}&endTime=${encodeURI(action.payload.endDate.toISOString())}`);

    yield response
        .FlatMap(success => put(ActionCreators.FetchReportSuccess(success.result) as any))
        .Reduce(error => put(ActionCreators.FetchReportFailed(error.text) as any))
}


function* handleIFetchReportXlsx(action: IFetchReportXlsx) {

    const response: Either<IError, ICallBlobResult> = yield call(downloadBlob, 'get', `${reportEndpointXlsx}?gebruikerId=${action.payload.gebruikersId}&startTime=${encodeURI(action.payload.startDate.toISOString())}&endTime=${encodeURI(action.payload.endDate.toISOString())}`);

    yield response
        .FlatMap(success => put(ActionCreators.FetchReportXlsxSuccess(success.result) as any))
        .Reduce(error => put(ActionCreators.FetchReportXlsxFailed(error.text) as any))

}

function* watchAll() {
    yield takeEvery(ActionTypes.FetchReport, handleIFetchReport);
    yield takeEvery(ActionTypes.FetchReportXlsx, handleIFetchReportXlsx);
}
export function* Sagas() {
    yield all([fork(watchAll)]);
}